import { useTranslation } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import { get } from "lodash";
import { useEffect, useState } from "react";
import ApiService from "utills/ApiService";
import { DocumentIcon, PencilIcon, PhoneIcon, SparklesIcon, UserIcon } from "@heroicons/react/outline";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";

const ProfilePage = () => {
  const [profile, setProfile] = useState(null as any);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState(null as any);
  const { translate, getLocale } = useTranslation();
  const [fetchData, setFetchData] = useState(false);
  const language = getLocale();
  const [token, setToken] = useState('');

  useEffect(() => {
    const user = localStorage.getItem("USER");

    if (user) {
      const userData = JSON.parse(user);
      setToken(userData?.token);
    }
  }, []);

  const breadcrumbItems = [
    {
      label: translate('my-profile'),
      href: ''
    }
  ];

  useEffect(() => {
    if (token !== '' && !profile) {
      const intervalFetchData = setInterval(() => {
        setFetchData(true);
        clearInterval(intervalFetchData);
      }, 100);
    }
  }, [profile, token]);

  useEffect(() => {
    if (fetchData) {
      if (!profile) {
        fetchProfile();
      } else {
        if (profile.events && profile.events.length > 0) {
          fetchEventList();
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, fetchData]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const result = await ApiService.get(`account/profile`, token);

      if (result) {
        setProfile(result);
      }
    } catch (error: any) {
      toast.error(error.message);
      setLoading(false);
      setFetchData(false);
    }
  }

  const fetchEventList = async () => {
    try {
      const result = await ApiService.get(`events`, token);

      if (result && result.length > 0) {

        const arr = [] as any;
        result.forEach((event: any) => {
          for (let i = 0; i < profile.events.length; i++) {
            if (profile.events[i] === event.id) {
              arr.push(get(event, `name_${language}`, ''));
            }
          }
        });

        setEvents([...arr]);
      }
    } catch (error: any) {
      toast.error(error.message);
    }

    setLoading(false);
    setFetchData(false);
  }

  return (
    <div className="w-full">
      <Breadcrumbs type="wps" breadcrumbItems={breadcrumbItems} />

      <div className="w-full p-[16px]">
        <div className="w-full">
          {
            profile &&
            <div className="w-full rounded-[4px] bg-white border border-[#eee]">
              <div className="w-full h-[64px] flex items-center justify-between px-[16px] border-b border-[#c5ccd2]">
                <div className="text-[14px] text-black font-bold">{translate('my-profile')}</div>

                <a href="/profile/edit">
                  <div className="h-[42px] px-[16px] cursor-pointer rounded-[4px] flex items-center justify-center border border-brand-500 text-14 text-brand-500 font-600 gap-x-[8px]">
                    <PencilIcon className="w-4 h-4 text-brand-500" strokeWidth={2} />
                    {translate("btn-edit")}
                  </div>
                </a>
              </div>

              <div className="w-full p-[24px]">
                <div className="size-[88px] mb-[24px]">
                  <img src={(profile.avatar && profile.avatar !== '') ? profile.avatar : "https://lh3.googleusercontent.com/a/default-user=s96-c"}
                    alt="user-avatar" className="size-[88px] rounded-full" />
                </div>

                <div className={`w-full grid grid-cols-[200px_auto] md:grid-cols-[465px_auto] gap-5 min-h-[72px] py-[16px] border-b border-[#c5ccd2]`}>
                  <div className="flex items-center gap-x-[16px]">
                    <div className="size-[40px] rounded-full bg-[#feefe7] flex items-center justify-center">
                      <UserIcon className="size-[20px] text-[#f1600d]" strokeWidth={2} />
                    </div>

                    <div className="label">{translate('full-name')}</div>
                  </div>

                  <div className="flex items-center justify-end md:justify-start">
                    <span className="max-w-[120px] md:max-w-[200px] lg:max-w-[300px] xl:max-w-[400px] text-14 break-words">
                      {profile.first_name} {profile.last_name}
                    </span>
                  </div>
                </div>

                <div className="w-full grid grid-cols-[200px_auto] md:grid-cols-[465px_auto] gap-5 min-h-[72px] py-[16px] border-b border-[#c5ccd2]">
                  <div className="flex items-center gap-x-[16px]">
                    <div className="size-[40px] rounded-full bg-[#feefe7] flex items-center justify-center">
                      <DocumentIcon className="size-[20px] text-[#f1600d]" strokeWidth={2} />
                    </div>

                    <div className="label">{translate('role-name')}</div>
                  </div>

                  <div className="flex items-center justify-end md:justify-start">
                    <span className="max-w-[120px] md:max-w-[200px] lg:max-w-[300px] xl:max-w-[400px] text-14 break-words">
                      {profile.role}
                    </span>
                  </div>
                </div>

                <div className="w-full grid grid-cols-[200px_auto] md:grid-cols-[465px_auto] gap-5 min-h-[72px] py-[16px] border-b border-[#c5ccd2]">
                  <div className="flex items-center gap-x-[16px]">
                    <div className="size-[40px] rounded-full bg-[#feefe7] flex items-center justify-center">
                      <SparklesIcon className="size-[20px] text-[#f1600d]" strokeWidth={2} />
                    </div>

                    <div className="label">{translate('event')}</div>
                  </div>

                  {
                    events && events.length > 0 &&
                    <div className="flex flex-col gap-3 justify-center items-end md:items-start">
                      {
                        events.map((item: any, index: number) => {
                          return (
                            <span
                              key={`event-name-${index}`}
                              className="max-w-[120px] md:max-w-[200px] lg:max-w-[300px] xl:max-w-[400px] text-14 break-words"
                            >
                              {item}
                            </span>
                          )
                        })
                      }
                    </div>
                  }

                </div>

                <div className="w-full grid grid-cols-[200px_auto] md:grid-cols-[465px_auto] gap-5 min-h-[72px] py-[16px] border-b border-[#c5ccd2]">
                  <div className="flex items-center gap-x-[16px]">
                    <div className="size-[40px] rounded-full bg-[#feefe7] flex items-center justify-center">
                      <PhoneIcon className="size-[20px] text-[#f1600d]" strokeWidth={2} />
                    </div>

                    <div className="label">{translate('contact-phone')}</div>
                  </div>

                  <div className="flex items-center justify-end md:justify-start">
                    <span className="max-w-[120px] md:max-w-[200px] lg:max-w-[300px] xl:max-w-[400px] text-14 break-words">
                      +{profile.phone_area_code} {profile.phone}
                    </span>
                  </div>
                </div>

                <div className="w-full grid grid-cols-[200px_auto] md:grid-cols-[465px_auto] gap-5 min-h-[72px] py-[16px]">
                  <div className="flex items-center gap-x-[16px]">
                    <div className="size-[40px] rounded-full bg-[#feefe7] flex items-center justify-center">
                      <img src="images/logo/mail.svg" alt="icon-email" />
                    </div>

                    <div className="label">Email</div>
                  </div>

                  <div className="flex items-center justify-end md:justify-start">
                    <span className="max-w-[120px] md:max-w-[200px] lg:max-w-[300px] xl:max-w-[400px] text-14 break-words">
                      {profile.email}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          }

          <div className="w-full h-[64px] p-[16px] flex items-center justify-between bg-white border border-[#eee] rounded-[4px] mt-[8px]">
            <div className="text-16 font-600">{translate('manage-password')}</div>

            <a href="/profile/change-password">
              <div className="h-[42px] px-[16px] flex items-center cursor-pointer border border-brand-500 text-brand-500 text-14 font-600 rounded-[4px]">
                <PencilIcon className="w-4 h-4 text-brand-500" strokeWidth={2} />
                <span className="ml-[8px]">{translate("change-password")}</span>
              </div>
            </a>
          </div>

          {
            loading &&
            <FullScreenLoading />
          }
        </div>
      </div>

    </div>
  )
};

export default ProfilePage;